/**
 * Which objects of a room are EXISTING conditions — there before anyone
 * designed anything — and which of those are fixed to the house
 * (claude-docs/2026-10-15-inspection-report-items-design.md). One rule for the
 * inspection's targets and the report's plans. Web-safe: no Node built-ins
 * (`surfaceTargets.js` needs `crypto`, so the rule cannot live there). Pure.
 */

const {
  PIPELINE_PRODUCERS,
  GRID_PRODUCERS,
  BATH_ASSEMBLY_PRODUCERS,
} = require('../scanned-label/producers');

/**
 * What the scan pipeline builds from the scan while it is processed —
 * `processStructure`'s post-processing (kitchen, bathroom vanity, the
 * design-adjustment rules, a basin RoomPlan dropped and the depth recovered)
 * and step 10b, which re-solves a scanned run or island into cabinetry. A
 * kitchen's cabinets and every countertop come from here: RoomPlan captures
 * storage boxes and no countertop at all. The scanned label's list, shared,
 * less the pipeline's light and switch grid, which is its guess and not
 * something the scan found, and less the tub and shower tools, which a
 * design uses under the same name (`producers.js`).
 */
const SCAN_TIME_PRODUCERS = new Set(
  [...PIPELINE_PRODUCERS].filter(
    (p) => !GRID_PRODUCERS.has(p) && !BATH_ASSEMBLY_PRODUCERS.has(p)
  )
);

/**
 * Design state, not existing conditions: a generated kitchen layout's slab and a
 * user-drawn cabinet run's. Asking the walkthrough about one asks about a counter
 * that was never built.
 */
const DESIGN_COUNTERTOP_PRODUCERS = new Set([
  'kitchen-layout',
  'manual-cabinet-run',
]);

/** The cabinet object types in use (the corpus, 2026-10-15). */
const CABINET_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'tall-cabinet',
  'vanity-cabinet',
  'sink-cabinet',
  'leg-cabinet',
  'wall-cabinet',
]);

/** The object types the existing-conditions pass asks about. */
const INSPECTABLE_TYPES = new Set([
  'sink',
  'toilet',
  'bathtub',
  'shower',
  'refrigerator',
  'dishwasher',
  'oven',
  'cooktop',
  'range',
  'stove',
  'washerDryer',
  'fireplace',
  'storage',
  'countertop',
  ...CABINET_TYPES,
]);

/** RoomPlan-captured: it carries RoomPlan's identifier and nothing generated it. */
const isScanCaptured = (o) =>
  !!o && typeof o.identifier === 'string' && !o.addedBy;

/** Built by the scan pipeline from the scan (`SCAN_TIME_PRODUCERS`). */
const isScanBuilt = (o) => !!o && SCAN_TIME_PRODUCERS.has(o.addedBy);

/** Cabinetry a countertop can stand on as an existing condition. */
const isExistingBase = (o) => isScanCaptured(o) || isScanBuilt(o);

/**
 * A countertop is existing when it stands on existing cabinetry or the scan
 * pipeline built it. A design's slab is never one, whatever it stands on.
 */
function isExistingCountertop(o, objectsById) {
  if (DESIGN_COUNTERTOP_PRODUCERS.has(o.addedBy)) return false;
  const parentId = o.parents && o.parents[0] && o.parents[0].id;
  if (parentId && isExistingBase(objectsById.get(parentId))) return true;
  return isScanBuilt(o);
}

/** There before any design: captured by the scanner, or built from the scan. */
function isExistingObject(o, objectsById) {
  if (!o) return false;
  if (o.type === 'countertop') return isExistingCountertop(o, objectsById);
  return isExistingBase(o);
}

/**
 * Fixed to the house and existing — what the inspection inspects and the
 * report tags. No light type is among them: the scanner captures no lights,
 * and the ones in a room doc are the pipeline's or a design's.
 */
function isFixed(o, objectsById) {
  return (
    !!o && INSPECTABLE_TYPES.has(o.type) && isExistingObject(o, objectsById)
  );
}

/** Captured by the scanner and not fixed: beds, tables, chairs, sofas, TVs. */
function isFurniture(o) {
  return isScanCaptured(o) && !INSPECTABLE_TYPES.has(o.type);
}

/**
 * Whether a fixed object may really be furniture: the one mistake a scan makes
 * here is RoomPlan's `storage` class, which takes a dresser or a bookcase as
 * readily as a built-in cabinet. The contractor's "Not built in" switch is
 * offered for these alone — a countertop, a toilet, a cabinet rebuilt from the
 * scan or an appliance is never that mistake.
 */
const mayBeFurniture = (type) => type === 'storage';

module.exports = {
  mayBeFurniture,
  SCAN_TIME_PRODUCERS,
  CABINET_TYPES,
  INSPECTABLE_TYPES,
  isScanCaptured,
  isScanBuilt,
  isExistingObject,
  isFixed,
  isFurniture,
};
